/**
 * One row per publisher, reconciled across the three things that know about one.
 *
 * The roster is what each client says about itself, and it is the only source
 * for whether their camera is blocked. The media server's stream list is what
 * actually exists, and it is right even when a client is wedged or on an old
 * build. Read separately they had to be joined by eye, and the case worth
 * catching - a client reporting a publisher the session does not have - was
 * only visible to somebody comparing two tables.
 */
import { MonitoredStream } from '../../hooks/health/use-session-monitor';
import { ConnectedUser } from '../../stores/types/connection';
import { getProfileFromStreamName, isStreamScreenshare } from '../../utils/utils';

/** Where the roster and the media server disagree about what is published. */
export type Discrepancy =
	/** A client reports a publisher id the session has no stream for. */
	| 'no-stream'
	/** A stream naming a gr_user the roster has never listed. */
	| 'unclaimed';

export interface SessionRow {
	streamId?: string;
	name?: string;
	hasAudio: boolean;
	hasVideo: boolean;
	creationTime?: number;
	grUser?: number;
	source: StreamSource;
	width?: number;
	height?: number;
	user?: ConnectedUser;
	departed: boolean;
	discrepancy?: Discrepancy;
}

export type StreamSource = 'camera' | 'screenshare' | 'virtual-background' | 'custom';

/*
	Only the sources worth calling out - a camera is the norm and needs no badge.

	`custom` stays deliberately vague: it means a track this app did not publish,
	so anything more specific would be a guess.
*/
export const SOURCE_LABELS: Record<StreamSource, string> = {
	camera: '',
	screenshare: 'screenshare',
	'virtual-background': 'virtual background',
	custom: 'custom source',
};

/**
 * The camera publisher is the only thing that names a stream `<gr_user>,<guid>`.
 * Used to tell an in-room participant's feed from one published by something
 * outside this app, whose custom tracks mean something we cannot name.
 */
const isRoomCameraName = (name?: string): boolean => (
	!!name && name.includes(',') && Number.isFinite(Number(name.split(',')[0]))
);

/**
 * `videoType` is derived from what was handed to initPublisher, so it says
 * `custom` for anything published from a MediaStreamTrack - which covers both a
 * virtual background and a screenshare that had to be restarted from a stored
 * track. Neither is what a producer reads "custom source" as meaning.
 */
export const streamSource = (stream: MonitoredStream): StreamSource => {
	// Name first: the screenshares that cannot report `source: 'screen'` are
	// deliberately named so they stay detectable. See opentok-context's
	// `secondary-screen` calls.
	if (isStreamScreenshare(stream)) return 'screenshare';

	// The only MediaStreamTrack a room camera publishes from is the MediaPipe
	// background pipeline. Off a stream this app did not publish, a custom track
	// could be anything, so it keeps the label that claims nothing.
	if (stream.videoType === 'custom') {
		return isRoomCameraName(stream.name) ? 'virtual-background' : 'custom';
	}

	return 'camera';
};

/**
 * Greenroom names a camera stream `<gr_user>,<guid>`, a mobile one `mob-<id>`,
 * and a browser-picker screenshare with the bare gr_user.
 */
export const grUserFromName = (name?: string): number | undefined => {
	if (!name) return undefined;
	const grUser = getProfileFromStreamName(name);
	return Number.isFinite(grUser) && grUser > 0 ? grUser : undefined;
};

interface RowInput {
	streams: MonitoredStream[];
	/** Everyone seen since the dashboard opened, so a departed publisher is named. */
	users: ConnectedUser[];
	/** Of those, who the latest poll still had in the room. */
	present: Set<number>;
	/**
	 * Whether the stream list can be trusted to be complete. Reconciling against
	 * an empty list because the monitor has not connected would report every
	 * publisher in the room as missing.
	 */
	connected: boolean;
}

export function sessionRows({
	streams, users, present, connected,
}: RowInput): SessionRow[] {
	const byGrUser = new Map(users.map((user) => [user.gr_user, user]));

	const rows: SessionRow[] = streams.map((stream) => {
		const grUser = grUserFromName(stream.name);
		const user = grUser ? byGrUser.get(grUser) : undefined;

		return {
			streamId: stream.streamId,
			name: stream.name,
			hasAudio: stream.hasAudio,
			hasVideo: stream.hasVideo,
			creationTime: stream.creationTime,
			grUser,
			source: streamSource(stream),
			width: stream.width,
			height: stream.height,
			user,
			departed: !!grUser && !present.has(grUser),
			// Only where the name claims a participant: a feed published from
			// outside greenroom has no roster entry to be missing from.
			discrepancy: grUser && !user ? 'unclaimed' : undefined,
		};
	});

	// `webrtcID` is the client's own record of the stream it published, so the
	// server either has that id or the client is wrong about publishing at all.
	const published = new Set(streams.map((stream) => stream.streamId));
	const missing = connected
		? users.filter((user) => (
			!!user.webrtcID
			&& present.has(user.gr_user)
			&& !published.has(String(user.webrtcID))
		))
		: [];

	return [
		...rows,
		...missing.map((user): SessionRow => ({
			grUser: user.gr_user,
			hasAudio: false,
			hasVideo: false,
			source: 'camera' as StreamSource,
			user,
			departed: false,
			discrepancy: 'no-stream' as Discrepancy,
		})),
	]
		// Oldest first, so the order matches the order the room filled up. A row
		// with no stream has no creation time, so it sorts to the top where a
		// discrepancy belongs.
		.sort((a, b) => (a.creationTime ?? 0) - (b.creationTime ?? 0));
}
